import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Pagination, PrimaryButton } from "widgets";
import { useSearchBranch, useSearchBranchesByName } from "store/backend/apiHooks";
import SearchBranch from "components/SearchBranch/SearchBranch";
import BranchesList from "components/BranchesList/BranchesList";
import { FileUploadOutlined } from "@mui/icons-material";
import Loader from "shared/Icons/Loader/Loader";
import { SEARCH_BRANCH_BY_CODE, SEARCH_BRANCH_BY_NAME, BRANCH_PAGE_SIZE_OPTIONS, DEFAULT_BRANCH_PAGE_NUMBER, MIN_BRANCH_NAME_SEARCH_LENGTH ,ZERO} from "constants/constants";

import styles from "./Branch.module.scss";

const handleOnClickClearButton = (
  setTenantCode,
  setBranchCode,
  setBranchName,
  setPage,
  setHasSearched,
  resetSearchResults,
) => {
  setTenantCode("");
  setBranchCode("");
  setBranchName("");
  setPage(DEFAULT_BRANCH_PAGE_NUMBER);
  setHasSearched(false);
  resetSearchResults();
};

const handleOnclickUploadBranchButton = (navigate) => {
  navigate("/uploadBranches");
};

const Branch = () => {
  const navigate = useNavigate();
  const [searchBranchBy, setSearchBranchBy] = useState(SEARCH_BRANCH_BY_CODE);
  const [tenantCode, setTenantCode] = useState("");
  const [branchCode, setBranchCode] = useState("");
  const [branchName, setBranchName] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(BRANCH_PAGE_SIZE_OPTIONS[0]);
  const [hasSearched, setHasSearched] = useState(false);
  const {
    mutate: searchBranch,
    data: branchDetails,
    isPending: isSearchBranchLoading,
    isError: isSearchBranchError,
    error: searchBranchError,
    reset: resetSearchBranch,
  } = useSearchBranch();
  const {
    mutate: searchBranchesByName,
    data: branchSearchDetails,
    isPending: isSearchBranchesByNameLoading,
    isError: isSearchBranchesByNameError,
    error: searchBranchesByNameError,
    reset: resetSearchBranchesByName,
  } = useSearchBranchesByName();

  const isSearchByName = searchBranchBy === SEARCH_BRANCH_BY_NAME;
  const isLoading = isSearchBranchLoading || isSearchBranchesByNameLoading;
  const isError = isSearchByName ? isSearchBranchesByNameError : isSearchBranchError;
  const error = isSearchByName ? searchBranchesByNameError : searchBranchError;
  const searchedBranchDetails = branchDetails?.data;
  const searchedBranchesByName = branchSearchDetails?.branches;
  const branches = isSearchByName
    ? searchedBranchesByName
    : searchedBranchDetails?.branchId
      ? [searchedBranchDetails]
      : [];
  const hasBranchPayload = Array.isArray(branches) && branches.length > ZERO;
  const totalCount = isSearchByName
    ? branchSearchDetails?.totalCount
    : hasBranchPayload
      ? DEFAULT_BRANCH_PAGE_NUMBER
      : undefined;
  const totalPages = branchSearchDetails?.totalPages ?? DEFAULT_BRANCH_PAGE_NUMBER;
  const currentPage = branchSearchDetails?.currentPage ?? page;
  const pageSize = branchSearchDetails?.pageSize ?? limit;
  const showPagination =
    isSearchByName &&
    hasSearched &&
    Array.isArray(searchedBranchesByName) &&
    !isLoading &&
    !isError;

  const resetSearchResults = () => {
    resetSearchBranch();
    resetSearchBranchesByName();
  };

  const executeNameSearch = (targetPage, targetLimit) => {
    const trimmedSearchValue = branchName.trim();
    if (!trimmedSearchValue || trimmedSearchValue.length < MIN_BRANCH_NAME_SEARCH_LENGTH) {
      return;
    }

    searchBranchesByName({
      branchName: trimmedSearchValue,
      page: targetPage,
      limit: targetLimit,
    });
  };

  const handleSearchClick = () => {
    resetSearchResults();
    setHasSearched(true);
    setPage(DEFAULT_BRANCH_PAGE_NUMBER);

    if (searchBranchBy === SEARCH_BRANCH_BY_CODE) {
      const trimmedTenantCode = tenantCode.trim();
      const trimmedBranchCode = branchCode.trim();
      if (!trimmedTenantCode || !trimmedBranchCode) {
        return;
      }
      searchBranch({
        tenantCode: trimmedTenantCode,
        branchCode: trimmedBranchCode,
      });
      return;
    }

    executeNameSearch(DEFAULT_BRANCH_PAGE_NUMBER, limit);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    executeNameSearch(nextPage, limit);
  };

  const handlePageSizeChange = (nextLimit) => {
    setLimit(nextLimit);
    setPage(DEFAULT_BRANCH_PAGE_NUMBER);
    executeNameSearch(DEFAULT_BRANCH_PAGE_NUMBER, nextLimit);
  };

  const handleSearchBranchByChange = (nextSearchBranchBy) => {
    setSearchBranchBy(nextSearchBranchBy);
    setPage(DEFAULT_BRANCH_PAGE_NUMBER);
    setHasSearched(false);
    resetSearchResults();
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.page}>
        <header className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Branches</h1>
          <div className={styles.pageActions}>
            <PrimaryButton
              onClick={() => handleOnclickUploadBranchButton(navigate)}
            >
              <FileUploadOutlined /> Upload Branch
            </PrimaryButton>
          </div>
        </header>
        <div className={styles.searchContainer}>
          <h2 className={styles.searchSectionTitle}>Search Branch</h2>
          <SearchBranch
            searchBranchBy={searchBranchBy}
            setSearchBranchBy={handleSearchBranchByChange}
            tenantCode={tenantCode}
            setTenantCode={setTenantCode}
            branchCode={branchCode}
            setBranchCode={setBranchCode}
            branchName={branchName}
            setBranchName={setBranchName}
            handleOnClickSearchButton={handleSearchClick}
            isLoading={isLoading}
            handleOnClickClearButton={() =>
              handleOnClickClearButton(
                setTenantCode,
                setBranchCode,
                setBranchName,
                setPage,
                setHasSearched,
                resetSearchResults,
              )
            }
            reset={resetSearchResults}
            isError={isError}
          />
        </div>
        <section
          className={styles.resultsPanel}
          aria-labelledby="branch-search-results-heading"
        >
          <header className={styles.resultsPanelHeader}>
            <div className={styles.resultsTitleRow}>
              <h2
                id="branch-search-results-heading"
                className={styles.resultsHeading}
              >
                Search Results
              </h2>
              {isLoading ? (
                <span className={styles.resultsHeaderMeta}>Searching…</span>
              ) : hasSearched && typeof totalCount === "number" ? (
                <span className={styles.resultsCountBadge} aria-live="polite">
                  {totalCount === DEFAULT_BRANCH_PAGE_NUMBER ? "1 branch" : `${totalCount} branches`}
                </span>
              ) : null}
            </div>
          </header>
          <div className={styles.resultsBody}>
            {isLoading ? (
              <div className={styles.resultsStateWrap}>
                <Loader />
              </div>
            ) : isError ? (
              <div className={styles.errorMessage}>
                {error?.message ||
                  "Something went wrong while fetching the branch."}
              </div>
            ) : searchBranchBy === SEARCH_BRANCH_BY_CODE && (!tenantCode.trim() || !branchCode.trim()) ? (
              <p className={styles.resultsEmpty}>
                Enter tenant code and branch code above, then choose{" "}
                <strong>Search</strong> to load the branch here.
              </p>
            ) : searchBranchBy === SEARCH_BRANCH_BY_NAME && branchName.trim().length < MIN_BRANCH_NAME_SEARCH_LENGTH ? (
              <p className={styles.resultsEmpty}>
                Enter at least 3 characters of the branch name above, then choose{" "}
                <strong>Search</strong> to load branches here.
              </p>
            ) : hasBranchPayload ? (
              <BranchesList branches={branches} />
            ) : hasSearched ? (
              <p className={styles.resultsEmpty}>
                No branch matched your criteria. Try a different code or name, or clear and
                search again.
              </p>
            ) : searchBranchBy === SEARCH_BRANCH_BY_CODE && tenantCode.trim() && branchCode.trim() ? (
              <p className={styles.resultsEmpty}>
                Tenant and branch codes are ready. Click <strong>Search</strong> to
                view the branch.
              </p>
            ) : searchBranchBy === SEARCH_BRANCH_BY_NAME && branchName.trim().length >= MIN_BRANCH_NAME_SEARCH_LENGTH ? (
              <p className={styles.resultsEmpty}>
                Branch name is ready. Click <strong>Search</strong> to view matching branches.
              </p>
            ) : null}
          </div>
          {showPagination && (
            <footer className={styles.resultsFooter}>
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                totalItems={totalCount}
                pageSize={pageSize}
                pageSizeOptions={BRANCH_PAGE_SIZE_OPTIONS}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                disabled={isLoading}
              />
            </footer>
          )}
        </section>
      </div>
    </div>
  );
};

export default Branch;
